import type {createAppRouter} from "@project/router";
import type {QueryClient} from "@tanstack/react-query";

import {I18nProvider, Toast} from "@project/design-system-web";
import {CopilotKitProvider} from "@project/extensions/ai-assistant";
import {createI18nRuntime} from "@project/i18n";
import {I18nRuntimeProvider, RawIntlProvider} from "@project/i18n/react";
import {QueryClientProvider} from "@tanstack/react-query";
import {useMemo} from "react";

import enGbMessages from "../../../locales/en-GB.json";

import TanStackRouterProvider from "./tanstack-router.provider";

interface RootProvidersProps {
  children?: React.ReactNode;
  queryClient: QueryClient;
  router: ReturnType<typeof createAppRouter>;
}

/**
 * Root provider tree for the web app.
 *
 * Persistence is handled per-query via the `persister` set on the QueryClient's
 * defaultOptions in App.tsx — no blocking restore gate is needed. Each query
 * self-restores lazily from localStorage when it first mounts; route loaders
 * calling `ensureQueryData` ensure data is in cache before components render.
 *
 * DevTools:
 * - Rendered directly in App.tsx (not in providers) so it's inside StrictMode
 * - Receives stable router and queryClient references from App.tsx module level
 * - Conditional activation via isDevModeEnabled toggle in Settings
 */
export default function RootProviders({children, queryClient, router}: RootProvidersProps) {
  const runtime = useMemo(() => createI18nRuntime({locale: "en-GB", messages: enGbMessages}), []);

  return (
    <QueryClientProvider client={queryClient}>
      <CopilotKitProvider>
        <I18nRuntimeProvider value={runtime}>
          <RawIntlProvider value={runtime.intl}>
            <I18nProvider locale={runtime.locale}>
              <Toast.Provider />
              <TanStackRouterProvider router={router}>{children}</TanStackRouterProvider>
            </I18nProvider>
          </RawIntlProvider>
        </I18nRuntimeProvider>
      </CopilotKitProvider>
    </QueryClientProvider>
  );
}
